import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CopyResultButton } from "@/components/CopyResultButton";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";

export default function ModifiedDurationCalculator() {
  const [macaulayDuration, setMacaulayDuration] = useState("");
  const [ytm, setYtm] = useState("");
  const [result, setResult] = useState<{ modDuration: number; priceChange: number } | null>(null);

  const calculate = () => {
    const duration = parseFloat(macaulayDuration);
    const yieldRate = parseFloat(ytm) / 100;

    if (isNaN(duration) || isNaN(yieldRate) || duration <= 0) {
      toast.error("Please enter valid values");
      return;
    }

    // Modified Duration = Macaulay Duration / (1 + YTM)
    const modDuration = duration / (1 + yieldRate);
    
    // Approximate price change for 1% yield change
    const priceChange = -modDuration * 0.01 * 100; // as percentage

    setResult({ modDuration, priceChange });
    toast.success("Modified Duration calculated!");
  };

  const reset = () => {
    setMacaulayDuration("");
    setYtm("");
    setResult(null);
  };

  return (
    <PageLayout>
      <Card>
        <CardHeader>
          <CardTitle>Modified Duration Calculator</CardTitle>
          <CardDescription>
            Measure bond price sensitivity to yield changes
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="duration">Macaulay Duration (years)</Label>
            <Input
              id="duration"
              type="number"
              placeholder="7.5"
              value={macaulayDuration}
              onChange={(e) => setMacaulayDuration(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="ytm">Yield to Maturity (%)</Label>
            <Input
              id="ytm"
              type="number"
              placeholder="6"
              value={ytm}
              onChange={(e) => setYtm(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <Button onClick={calculate}>Calculate</Button>
            <Button onClick={reset} variant="outline">Reset</Button>
          </div>
          {result !== null && (
            <>
              <div className="mt-4 space-y-3">
              <div className="p-4 bg-muted rounded-lg">
                <div className="flex items-center justify-between">
                  <p className="text-lg">
                    <strong>Modified Duration:</strong> {result.modDuration.toFixed(3)} years
                  </p>
                  <CopyResultButton value={result.modDuration.toFixed(3)} />
                </div>
              </div>
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm">
                  <strong>Price Change (1% yield change):</strong> {result.priceChange > 0 ? '+' : ''}{result.priceChange.toFixed(2)}%
                </p>
              </div>
            </div>

            <CalculatorSuperCalcCTA config={calculatorCTAs["modified-duration"]} trigger={result?.modDuration} />
            </>
          )}
        </CardContent>
      </Card>
    </PageLayout>
  );
}
